import React, { useState, useEffect, useMemo, useContext } from "react";
import { HexColorPicker, HexColorInput } from "react-colorful";
import { AuthContext } from "../contexts/auth";
import { Link, useLocation } from "wouter";
import Swal from "sweetalert2";
import { PencilSquareIcon, TrashIcon, MapPinIcon, XMarkIcon, TagIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { getWorkingAreaList, getWorkingAreaId, createWorkingArea, updateWorkingArea, patchWorkingArea, deleteWorkingArea } from '../services/api_working_area';
import { getJustificationCustomCompanyList } from "../services/api_justifications";
import { addAreaMarker, getAreaMarkerList } from '../services/api_justifications';
import { getNfcTagList } from "../services/api_nfc_tags";
import GeolocationModal from "./geolocation_modal";
import LocationMapField from "./location_map_field";
import Fetching from "./fetching";
import Empty from "./empty";
import WorkingAreaContextBar from "./working_area_context_bar";
import PopoverComponentCustom from '../components/popover_component';
import Footer from '../components/footer';
import ComboboxAutocomplete from './combobox_autocomplete';
import { estraiMessaggiErrore } from "../services/utils";
import TapAndPlayIcon from '@mui/icons-material/TapAndPlay';
import CustomTooltipComponent from "./custom_tooltip_component";
import PrettyPagination from "./pretty_pagination";
import WorkingAreaDetailRow from "./working_area_detail_row";

const todayISO = () => new Date().toISOString().slice(0, 10);

const isAreaActive = (area) => {
    const today = todayISO();

    if (area.valid_from && today < area.valid_from) {
        return false;
    }

    if (area.valid_to && today > area.valid_to) {
        return false;
    }

    return true;
};


const formatDate = (iso) => {
    if (!iso) return null;

    const [y, m, d] = iso.split("-");

    return `${d}/${m}/${y}`;
};


const emptyForm = {
    name: "",
    color: "",
    latitude: null,
    longitude: null,
    address: "",
    radius: "",
    valid_from: "",
    valid_to: "",
    justification: null,
    nfc_tag: null,
    notes: "",
    notification_timing: "",
    notification_email: "",
};

const WorkingAreaComponent = () => {
    const { profile, token, setProfile } = useContext(AuthContext)
    const isProfileComplete = profile.has_complete_profile;

    const [, setLocation] = useLocation();
    const [showInfoArea, setShowInfoArea] = useState(false);

    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [positionBeforeEdit, setPositionBeforeEdit] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [viewingArea, setViewingArea] = useState(null);
    const [isResolvingAddress, setIsResolvingAddress] = useState(false);
    const [expandedAreaId, setExpandedAreaId] = useState(null);

    const [selectedAreaIdentifier, setSelectedAreaIdentifier] = useState(null);

    const [customCompanyJr, setCustomCompanyJr] = useState([]);

    const [nfcTags, setNfcTags] = useState([]);
    const [selectedNfcTag, setSelectedNfcTag] = useState(null);
    const [loadingNfcTags, setLoadingNfcTags] = useState(false);

    const [showIdentifierForm, setShowIdentifierForm] = useState(false);
    const [identifierForm, setIdentifierForm] = useState({code: "", description: "",});
    const [identifierErrors, setIdentifierErrors] = useState({});
    const [isSavingIdentifier, setIsSavingIdentifier] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);

    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const totalPages = Math.ceil(totalCount / pageSize);

    const isEmployee = profile.customer_type === 'employee';

    const AREA_COLOR_PRESETS = [
        "#22c55e",
        "#3b82f6",
        "#6366f1",
        "#8b5cf6",
        "#ec4899",
        "#f59e0b",
        "#ef4444",
        "#14b8a6",
    ];

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 400);

        return () => clearTimeout(timeout);
    }, [search]);

    const fetchAreas = async () => {
        setLoading(true);

        try {
            const response = await getWorkingAreaList(token, {
                status: statusFilter,
                search: debouncedSearch || undefined,
                page,
                page_size: pageSize,
            }, 'shifts');
            setAreas(response.results || []);
            setTotalCount(response.count || 0);
        } catch (error) {
            console.error("Errore nel recupero delle aree di lavoro", error);

            Swal.fire({
                icon: "error",
                title: "Errore",
                text: estraiMessaggiErrore(error),
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchIdentifiers = async () => {
        if (!token) {
            setCustomCompanyJr([]);
            return [];
        }

        try {
            const response = await getJustificationCustomCompanyList(token);

            const customJustifications = Array.isArray(response)
                ? response
                : response?.results || [];

            setCustomCompanyJr(customJustifications);

            return customJustifications.filter(
                (item) => item?.is_marker_area === true
            );
        } catch (error) {
            console.error(
                "Errore nel recupero degli identificativi area",
                error
            );

            setCustomCompanyJr([]);

            Swal.fire({
                icon: "error",
                title: "Errore",
                text: estraiMessaggiErrore(error),
            });

            return [];
        }
    };

    const areaIdentifiers = useMemo(
        () =>
            customCompanyJr.filter(
                (item) => item?.is_marker_area === true
            ),
        [customCompanyJr]
    );

    useEffect(() => {
        if (!token) {
            setLoading(false);
            return;
        }

        fetchAreas(statusFilter);
    }, [token, statusFilter, page, pageSize, debouncedSearch]);

    useEffect(() => {
        if (totalPages > 0 && page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const fetchAvailableNfcTags = async () => {
        if (!token) {
            setNfcTags([]);
            return [];
        }

        setLoadingNfcTags(true);

        try {
            const response = await getNfcTagList(token, { filter_by: "unassigned", paginate: false, });
            const tags = Array.isArray(response) ? response : response?.results || [];
            setNfcTags(tags);
            return tags;
        } catch (error) {
            console.error("Errore caricamento tag NFC", error);

            Swal.fire({
                icon: "error",
                title: "Errore",
                text: estraiMessaggiErrore(error),
            });

            setNfcTags([]);

            return [];
        } finally {
            setLoadingNfcTags(false);
        }
    };

    const openCreateForm = async () => {
        setEditingId(null);
        setForm(emptyForm);
        setFormErrors({});
        setSelectedAreaIdentifier(null);
        setSelectedNfcTag(null);
        setNfcTags([]);
        setIsFormOpen(true);
        await Promise.all([
            fetchIdentifiers(),
            fetchAvailableNfcTags(),
        ]);
    };

    const openEditForm = async (area) => {
        setEditingId(area.id);
        setFormErrors({});
        setSelectedAreaIdentifier(null);
        setSelectedNfcTag(null);
        setNfcTags([]);
        setCustomCompanyJr([]);
        setIsFormOpen(true);

        setForm({
            name: area.name || "",
            color: area.color || "",
            latitude:
                area.latitude !== null &&
                area.latitude !== undefined
                    ? Number(area.latitude)
                    : null,
            longitude:
                area.longitude !== null &&
                area.longitude !== undefined
                    ? Number(area.longitude)
                    : null,
            address: area.address || "",
            radius: area.radius ?? "",
            valid_from: area.valid_from || "",
            valid_to: area.valid_to || "",
            justification:
                typeof area.justification === "object"
                    ? area.justification?.id ?? null
                    : area.justification ?? null,
            notes: area.notes || "",
            notification_timing: area.notification_timing ?? "",
            notification_email: area.notification_email || "",
        });

        const [identifiers, availableTags] = await Promise.all([
            fetchIdentifiers(),
            fetchAvailableNfcTags(),
        ]);

        const justificationId =
            typeof area.justification === "object"
                ? area.justification?.id
                : area.justification;

        const selectedIdentifier =
            identifiers.find(
                (item) =>
                    Number(item.id) ===
                    Number(justificationId)
            ) || null;

        setSelectedAreaIdentifier(selectedIdentifier);

        const currentNfc =
            typeof area.nfc_tag === "object"
                ? area.nfc_tag
                : null;

        if (currentNfc) {
            const alreadyPresent = availableTags.some(
                (tag) =>
                    Number(tag.id) ===
                    Number(currentNfc.id)
            );

            setNfcTags(
                alreadyPresent
                    ? availableTags
                    : [currentNfc, ...availableTags]
            );

            setSelectedNfcTag(currentNfc);
        }
    };

    const closeForm = () => {
        if (isSaving) {
            return;
        }
        setIsFormOpen(false);
        setEditingId(null);
        setForm(emptyForm);
        setIsPickerOpen(false);
        setPositionBeforeEdit(null);
        setSelectedAreaIdentifier(null);
        setShowIdentifierForm(false);
        setCustomCompanyJr([]);
        setIdentifierForm({code: "", description: "",});
        setIdentifierErrors({});
        setSelectedNfcTag(null);
        setNfcTags([]);
        setFormErrors({});
    };

    const openPositionPicker = () => {
        setPositionBeforeEdit({
            latitude: form.latitude,
            longitude: form.longitude,
            address: form.address,
            radius: form.radius,
        });

        setIsPickerOpen(true);
    };

    const cancelPositionPicker = () => {
        setIsResolvingAddress(false);

        setForm((prev) => ({
            ...prev,
            latitude: positionBeforeEdit?.latitude ?? null,
            longitude: positionBeforeEdit?.longitude ?? null,
            address: positionBeforeEdit?.address ?? "",
            radius: positionBeforeEdit?.radius ?? "",
        }));

        setPositionBeforeEdit(null);
        setIsPickerOpen(false);
    };

    const confirmPositionPicker = () => {
        setPositionBeforeEdit(null);
        setIsPickerOpen(false);
    };

    const validateIdentifierForm = () => {
        const errors = {};

        const code = identifierForm.code.trim();
        const description = identifierForm.description.trim();

        if (!code) {
            errors.code = "Il codice è obbligatorio";
        } else if (code.length > 3) {
            errors.code = "Il codice può contenere massimo 3 caratteri";
        }

        if (!description) {
            errors.description = "La descrizione è obbligatoria";
        }

        const duplicatedCode = customCompanyJr.some((item) => item.code?.trim() === code);

        if (duplicatedCode) {
            errors.code = "Questo codice è già presente";
        }

        setIdentifierErrors(errors);

        return Object.keys(errors).length === 0;
    };

    const handleCreateIdentifier = async () => {
        if (!validateIdentifierForm()) {
            return;
        }

        setIsSavingIdentifier(true);

        try {
            const newIdentifier = await addAreaMarker(token, {
                code: identifierForm.code.trim(),
                description: identifierForm.description.trim(),
                rateo: null,
            });

            setCustomCompanyJr((prev) => [
                ...prev,
                newIdentifier,
            ]);

            setSelectedAreaIdentifier(newIdentifier);

            setForm((prev) => ({
                ...prev,
                justification: newIdentifier.id,
            }));

            setIdentifierForm({
                code: "",
                description: "",
            });

            setIdentifierErrors({});
            setShowIdentifierForm(false);

            Swal.fire({
                icon: "success",
                title: "Identificativo creato",
                timer: 1000,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error(
                "Errore nella creazione dell'identificativo",
                error
            );

            const fieldErrors = error.response?.data;

            if (
                fieldErrors &&
                typeof fieldErrors === "object"
            ) {
                setIdentifierErrors(fieldErrors);
            }

            Swal.fire({
                icon: "error",
                title: "Errore",
                text: estraiMessaggiErrore(error),
            });
        } finally {
            setIsSavingIdentifier(false);
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!form.name?.trim()) {
            errors.name = "Il nome è obbligatorio";
        }

        if (
            form.radius !== "" &&
            (
                Number.isNaN(Number(form.radius)) ||
                Number(form.radius) < 0
            )
        ) {
            errors.radius = "Il raggio deve essere un numero positivo";
        }

        if (
            form.valid_from &&
            form.valid_to &&
            form.valid_from > form.valid_to
        ) {
            errors.valid_to =
                "La data 'al' non può precedere la data 'dal'";
        }

        if (form.notification_timing !== "") {
            const timing = Number(form.notification_timing);

            if (Number.isNaN(timing) || !Number.isInteger(timing) || timing < 1 || timing > 31) {
                errors.notification_timing = "Inserisci un numero di giorni compreso tra 1 e 31";
            }
        }

        if (form.notification_timing !== "" && !form.notification_email?.trim()) {
            errors.notification_email = "Inserisci almeno un indirizzo email per il promemoria";
        }

        if (form.notification_email?.trim() && form.notification_timing === "") {
            errors.notification_timing = "Indica quanti giorni prima della scadenza inviare il promemoria";
        }

        if (form.notification_email?.trim()) {
            const emails = form.notification_email.split(",").map((email) => email.trim()).filter(Boolean);

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            if (emails.length === 0 || emails.some((email) => !emailRegex.test(email))) {
                errors.notification_email = "Inserisci indirizzi email validi separati da virgola";
            } else {
                const normalizedEmails = emails.map((email) => email.toLowerCase()
                );

                const hasDuplicates = new Set(normalizedEmails).size !== normalizedEmails.length;

                if (hasDuplicates) {
                    errors.notification_email = "Sono presenti indirizzi email duplicati";
                }
            }
        }

        setFormErrors(errors);

        return Object.keys(errors).length === 0;
    };

    const handleSubmitForm = async () => {
        if (showIdentifierForm) {
            await Swal.fire({
                icon: "warning",
                title: "Identificativo non salvato",
                text: "Hai aperto il modulo per creare un nuovo identificativo. Prima di salvare l’area devi creare l’identificativo oppure annullare la compilazione.",
                confirmButtonText: "Chiudi",
            });

            return;
        }

        if (!validateForm()) return;

        setIsSaving(true);

        const payload = {
            name: form.name.trim(),
            color: form.color || null,
            address: form.address?.trim() || null,
            latitude: form.latitude !== null ? Number(form.latitude) : null,
            longitude: form.longitude !== null ? Number(form.longitude) : null,
            radius: form.radius === "" ? null : Number(form.radius),
            valid_from: form.valid_from || null,
            valid_to: form.valid_to || null,
            justification: form.justification || null,
            nfc_tag_id: selectedNfcTag?.id ?? null,
            notes: form.notes?.trim() || null,
            notification_timing: form.valid_to && form.notification_timing !== "" ? Number(form.notification_timing) : null,
            notification_email: form.valid_to && form.notification_email?.trim()
                ? form.notification_email.split(",").map((email) => email.trim()).filter(Boolean).join(",")
                : null,
        };

        try {
            if (editingId) {
                await patchWorkingArea(token, editingId, payload, 'shifts');
                await fetchAreas();
            } else {
                await createWorkingArea(token, payload, 'shifts');
                if (page !== 1) {
                    setPage(1);
                } else {
                    await fetchAreas();
                }
            }

            closeForm();

            Swal.fire({
                icon: "success",
                title: editingId ? "Area aggiornata" : "Area creata",
                timer: 1200,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("Errore nel salvataggio dell'area di lavoro", error);

            const fieldErrors = error.response?.data;

            if (fieldErrors && typeof fieldErrors === "object") {
                setFormErrors(fieldErrors);
            }

            Swal.fire({
                icon: "error",
                title: "Errore",
                text: estraiMessaggiErrore(error),
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (area) => {
        const confirm = await Swal.fire({
            icon: "warning",
            title: "Eliminare l'area di lavoro?",
            text: `"${area.name}" verrà eliminata.`,
            showCancelButton: true,
            confirmButtonText: "Elimina",
            cancelButtonText: "Annulla",
            confirmButtonColor: "#dc2626",
        });

        if (!confirm.isConfirmed) return;

        try {
            await deleteWorkingArea(token, area.id, 'shifts');

            if (areas.length === 1 && page > 1) {
                setPage((prev) => prev - 1);
            } else {
                await fetchAreas();
            }

            Swal.fire({
                icon: "success",
                title: "Area eliminata",
                timer: 1200,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error(
                "Errore nell'eliminazione dell'area di lavoro",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Errore",
                text: estraiMessaggiErrore(error),
            });
        }
    };

    const handlePositionChange = ([lat, lng]) => {
        setForm((prev) => ({
            ...prev,
            latitude: lat,
            longitude: lng,
        }));

        setIsResolvingAddress(true);
    };

    const handleAddressResolved = (address) => {
        setForm((prev) => ({
            ...prev,
            address: address || prev.address,
        }));

        setIsResolvingAddress(false);
    };

    const clearPosition = () => {
        setIsResolvingAddress(false);

        setForm((prev) => ({
            ...prev,
            latitude: null,
            longitude: null,
            // L'indirizzo rimane indipendente dalla posizione sulla mappa.
            // Rimuovere il pin non cancella il testo inserito dall'utente.
            radius: "",
        }));
    };

    return (

        <div className='flex flex-col min-h-screen'>
            <PopoverComponentCustom title="Gestisci Aree Lavoro" />

            {profile?.customer_type === 'company' && !isProfileComplete && profile?.company?.email == profile?.the_real_user_email && (
                <div className="w-full">
                    <div className="bg-orange-100 text-orange-600 p-4 mb-6">
                        <p className="text-center text-sm font-semibold">
                            Il profilo non è completo. <br />
                            Compila i campi mancanti{' '}
                            <Link
                                to="/profilo/fatturazione"
                                className="text-indigo-600 hover:text-indigo-800 underline"
                            >
                                cliccando qui
                            </Link>
                        </p>
                    </div>
                </div>
            )}
            <main className="flex-grow mt-10 pb-8">
                <div className='mx-auto w-full max-w-[1600px] px-4 md:px-6'>
                    <div>
                        <WorkingAreaContextBar
                            search={search}
                            onSearchChange={(value) => {setSearch(value); setPage(1);}}
                            statusFilter={statusFilter}
                            onStatusFilterChange={(value) => {setStatusFilter(value); setPage(1);}}
                            pageSize={pageSize}
                            onPageSizeChange={(value) => {setPageSize(Number(value)); setPage(1);}}
                            onAddClick={openCreateForm}
                            onManageIdentifiersClick={() => setLocation("/working_area/area_marker")}
                            onManageNfcClick={() => setLocation("/working_area/nfc")}
                            totalCount={totalCount}
                            filteredCount={areas.length}
                            stickyClassName=""
                            isEmployee={isEmployee}
                        />
                        <div className="mt-5 overflow-hidden rounded-xl border border-indigo-100 bg-indigo-50/40">
                            <button
                                type="button"
                                onClick={() => setShowInfoArea((prev) => !prev)}
                                aria-expanded={showInfoArea}
                                className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition-colors hover:bg-indigo-50 sm:px-5"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-indigo-600 ring-1 ring-indigo-100">
                                        <MapPinIcon className="h-5 w-5" />
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-semibold uppercase tracking-wide text-indigo-800">
                                            Aree di lavoro
                                        </h3>

                                        <p className="mt-0.5 text-xs text-gray-500">
                                            Scopri come funzionano pianificazione, timbrature e giustificativi area
                                        </p>
                                    </div>
                                </div>

                                <ChevronDownIcon
                                    className={`h-5 w-5 shrink-0 text-indigo-500 transition-transform duration-200 ${
                                        showInfoArea ? "rotate-180" : ""
                                    }`}
                                />
                            </button>

                            {showInfoArea && (
                                <div className="border-t border-indigo-100 px-4 py-4 sm:px-5">
                                    <p className="text-sm leading-6 text-gray-600 text-justify">
                                        Le <strong>aree di lavoro</strong> permettono di identificare sedi, cantieri o altre
                                        zone operative nelle quali viene svolta l'attività lavorativa. Associando un'area ai
                                        turni assegnati ai dipendenti è possibile confrontare, nella sezione
                                        <strong> Statistiche</strong>, le ore pianificate con quelle confermate all'interno
                                        della specifica area.
                                    </p>

                                    <p className="mt-3 text-sm leading-6 text-gray-600 text-justify">
                                        Durante l'assegnazione dei turni è possibile associare un'area di lavoro a una o più
                                        fasce orarie. A ciascuna area può inoltre essere collegato un
                                        <strong> giustificativo area</strong>, che rappresenta il riferimento utilizzato per
                                        la successiva elaborazione delle statistiche. La durata del giustificativo è sempre
                                        quella della fascia oraria pianificata e non dipende dalla durata della timbratura.
                                    </p>

                                    <p className="mt-3 text-sm leading-6 text-gray-600 text-justify">
                                        La timbratura può essere effettuata tramite la normale funzione di timbratura con
                                        geolocalizzazione disponibile nell'app del dipendente oppure mediante
                                        <strong> timbratura NFC</strong>, utilizzando un apposito dispositivo installato
                                        presso il luogo di lavoro. Entrambe le modalità consentono di rilevare la posizione
                                        dell'ingresso e verificare se la timbratura è stata eseguita all'interno dell'area
                                        prevista.
                                    </p>

                                    <p className="mt-3 text-sm leading-6 text-gray-600 text-justify">
                                        La posizione rilevata dalla timbratura viene utilizzata esclusivamente per
                                        <strong> confermare o meno il giustificativo area</strong> associato al turno.
                                        La timbratura <strong> non modifica la durata del giustificativo</strong> e non viene
                                        utilizzata per determinare le ore effettivamente lavorate: eventuali ingressi
                                        anticipati, ritardi o uscite posticipate non alterano la durata pianificata del turno,
                                        ma consentono solamente di verificare se l'attività è stata svolta nell'area prevista.
                                    </p>

                                    <p className="mt-3 text-sm leading-6 text-gray-600 text-justify">
                                        Le timbrature registrate consentono inoltre di monitorare la presenza dei dipendenti
                                        nelle diverse aree operative. Dalla sezione
                                        <strong> Presenze Area Lavoro</strong> è possibile consultare gli ingressi e le uscite
                                        della giornata, verificare le presenze ancora aperte e controllare in tempo reale la
                                        situazione delle singole aree di lavoro.
                                    </p>

                                    <p className="mt-3 text-sm leading-6 text-gray-600 text-justify">
                                        Nella sezione <strong>Statistiche</strong> è infine possibile confrontare, per ogni
                                        area di lavoro, le ore pianificate con le ore confermate mediante i relativi
                                        giustificativi, verificando così il corretto svolgimento della pianificazione nelle
                                        diverse aree operative.
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="rounded-lg mt-6 border border-gray-300">
                            <div className="w-full overflow-x-auto rounded-lg">
                                <table className="table-fixed w-full border-collapse text-sm">
                                    <thead className="sticky top-0 z-20 bg-gray-50">
                                        <tr className="bg-gray-50">
                                            <th className="w-[8%] pr-4 pl-2 py-2 text-left font-semibold text-gray-600 border-r border-gray-300">
                                                Giust.Id
                                            </th>
                                            <th className="w-[5%] px-2 py-2 text-center font-semibold text-gray-600 border-r border-gray-300">
                                                NFC
                                            </th>
                                            <th className="w-[20%] px-4 py-2 text-left font-semibold text-gray-600 border-r border-gray-300">
                                                Nome
                                            </th>
                                            <th className="w-[17%] px-4 py-2 text-left font-semibold text-gray-600 border-r border-gray-300 hidden sm:table-cell">
                                                Posizione
                                            </th>

                                            <th className="w-[10%] px-4 py-2 text-left font-semibold text-gray-600 border-r border-gray-300 hidden sm:table-cell">
                                                Raggio
                                            </th>

                                            <th className="w-[20%] px-4 py-2 text-left font-semibold text-gray-600 border-r border-gray-300 hidden sm:table-cell">
                                                Validità
                                            </th>

                                            <th className="w-[10%] px-4 py-2 text-left font-semibold text-gray-600 border-r border-gray-300 hidden sm:table-cell">
                                                Stato
                                            </th>

                                            <th className="w-[8%] px-4 py-2 text-center font-semibold text-gray-600">
                                                Azioni
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4">
                                                    <Fetching />
                                                </td>
                                            </tr>
                                        ) : areas.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="px-6 py-8">
                                                    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-gray-200 bg-gray-50 px-6 py-10 text-center">
                                                        <MapPinIcon className="mb-4 h-10 w-10 text-gray-400" />
                                                        <h3 className="text-sm font-semibold text-gray-800">
                                                            Nessuna area di lavoro presente
                                                        </h3>
                                                        <p className="mt-2 max-w-md text-sm text-gray-500">
                                                            Non hai ancora creato alcuna area di lavoro. Premi{" "}
                                                            <span className="font-medium text-indigo-600">
                                                            "Aggiungi area"
                                                            </span>{" "} per aggiungerne una.
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            areas.map((area) => {
                                            const active = isAreaActive(area);
                                            const hasPosition = area.latitude != null && area.longitude != null;
                                            const isExpanded = expandedAreaId === area.id;

                                            return (
                                                <React.Fragment key={area.id}>
                                                <tr
                                                    className={`border-t border-gray-300 cursor-pointer hover:bg-indigo-50/40 ${isExpanded ? "bg-indigo-50/60" : ""}`}
                                                    onClick={() =>
                                                        setExpandedAreaId((prev) => (prev === area.id ? null : area.id))
                                                    }
                                                >
                                                    <td className="px-4 py-2 border-r border-gray-300">
                                                        {area?.justification_code ? (
                                                            <span className="text-indigo-700 font-semibold text-medium">{area?.justification_code}</span>
                                                        ) : (<span className="text-gray-300">—</span>)}
                                                    </td>
                                                    <td className="border-r border-gray-300 px-4 py-2 align-middle font-medium text-gray-900">
                                                        <div className="flex h-full items-center justify-center">
                                                            {area?.nfc_tag ? (
                                                                <CustomTooltipComponent title={area.nfc_tag.name}>
                                                                    <TapAndPlayIcon className="!h-[19px] !w-[19px] text-indigo-700" />
                                                                </CustomTooltipComponent>
                                                            ) : (
                                                                <span className="text-gray-300">—</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-2 border-r border-gray-300 font-medium text-gray-900 whitespace-normal break-words">
                                                        {area?.name}
                                                    </td>

                                                    <td className="px-4 py-2 border-r border-gray-300 hidden sm:table-cell">
                                                        {hasPosition ? (
                                                            <button
                                                                type="button"
                                                                className="flex items-center gap-1 text-indigo-600 hover:underline"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setViewingArea(area);
                                                                }}
                                                            >
                                                                <MapPinIcon className="w-4 h-4 shrink-0" />
                                                                Visualizza
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-400 text-xs">Non impostata</span>
                                                        )}
                                                    </td>

                                                    <td className="px-4 py-2 border-r border-gray-300 hidden sm:table-cell">
                                                        {area?.radius ? `${area.radius} m` : (<span className="text-gray-300">—</span>)}
                                                    </td>

                                                    <td className="px-4 py-2 border-r border-gray-300 hidden sm:table-cell">
                                                            {area.valid_from || area.valid_to ? (
                                                            <span>
                                                                {formatDate(area.valid_from) || "…"} →{" "}
                                                                {formatDate(area.valid_to) || "…"}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400 text-xs">Sempre attiva</span>
                                                        )}
                                                    </td>

                                                    <td className="px-4 py-2 border-r border-gray-300 hidden sm:table-cell">
                                                        <span
                                                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                                active
                                                                ? "bg-green-100 text-green-700"
                                                                : "bg-gray-200 text-gray-600"
                                                            }`}
                                                        >
                                                            {active ? "Attiva" : "Non attiva"}
                                                        </span>
                                                    </td>

                                                    <td className="px-4 py-2 border-r border-gray-300 align-middl">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <button
                                                                className="hover:text-indigo-600"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openEditForm(area);
                                                                }}
                                                            >
                                                                <PencilSquareIcon className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                className="hover:text-red-600"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(area);
                                                                }}
                                                            >
                                                                <TrashIcon className="w-5 h-5" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>

                                                {isExpanded && (
                                                    <tr className="border-t border-gray-200 bg-indigo-50/30">
                                                        <td colSpan="8" className="px-4 py-4">
                                                            <WorkingAreaDetailRow area={area} />
                                                        </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            );})
                                        )}
                                    </tbody>
                                </table>
                                {totalPages && totalPages > 1 ? (
                                    <div className="pb-2 border-t border-gray-200">
                                        <PrettyPagination
                                            page={page}
                                            totalPages={totalPages}
                                            totalCount={totalCount}
                                            onChange={(newPage) => {
                                                setPage(newPage);
                                                setExpandedAreaId(null);

                                                window.scrollTo({top: 0, behavior: "smooth",});
                                            }}
                                            labelSingolare="area"
                                            labelPlurale="aree"
                                            className="mt-4"
                                        />
                                    </div>
                                ) : ('')}
                            </div>
                        </div>

                        {/* Modale di visualizzazione posizione */}
                        {viewingArea && (
                            <GeolocationModal
                                isOpen={!!viewingArea}
                                onClose={() => setViewingArea(null)}
                                locations={[
                                    {
                                        ...viewingArea,
                                        latitude: Number(viewingArea.latitude),
                                        longitude: Number(viewingArea.longitude),
                                    },
                                ]}
                                radius={viewingArea.radius}
                                title="Area di lavoro"
                                address={viewingArea?.address}
                            />
                        )}

                        {/* Modale creazione / modifica area di lavoro */}
                        {isFormOpen && (
                            <div className="fixed inset-0 flex items-start justify-center bg-black/50 px-4 py-6 sm:py-10" style={{ zIndex: 200 }}>
                                <div className="flex max-h-[calc(100vh-5rem)] w-full max-w-[50%] flex-col overflow-visible rounded-lg bg-white shadow-xl">
                                    <div className="shrink-0 flex items-center justify-between border-b border-gray-200 px-5 py-4 bg-indigo-100 rounded-t-lg">
                                        <h3 className="text-md uppercase font-semibold text-indigo-800">
                                            {editingId ? "Modifica area di lavoro" : "Nuova area di lavoro"}
                                        </h3>
                                        <button
                                            type="button"
                                            onClick={closeForm}
                                            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 uppercase"
                                        >
                                            Chiudi
                                        </button>
                                    </div>

                                    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                                        {/* Nome */}
                                        <div className="space-y-1 pb-3">
                                            <label className="block text-sm font-medium text-gray-900">
                                                Nome <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={form.name}
                                                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                                                className={`w-full py-1.5 px-2 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500 placeholder:text-sm placeholder:text-gray-400 placeholder:font-medium  ${
                                                    formErrors.name ? "border-red-400" : "border-gray-300"
                                                }`}
                                                placeholder="Es. Magazzino Nord"
                                            />
                                            {formErrors.name && (
                                                <p className="mt-1 text-xs text-red-600">
                                                    {Array.isArray(formErrors.name) ? formErrors.name[0] : formErrors.name}
                                                </p>
                                            )}
                                        </div>
                                        <hr className="h-0.5 bg-indigo-500 mb-2 " />
                                        {/* Colore area */}
                                        <div className="space-y-2 pb-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-900">
                                                        Colore Area
                                                        <span className="ml-1 text-xs font-medium text-gray-400">
                                                            (facoltativo)
                                                        </span>
                                                    </label>

                                                    <p className="mt-0.5 text-xs text-gray-500">
                                                        Utilizzato per identificare rapidamente l'area nella pianificazione. Se non impostato verrà utilizzato il colore predefinito
                                                    </p>
                                                </div>

                                                {form.color && (
                                                    <button 
                                                        type="button"
                                                        onClick={() => setForm((prev) => ({...prev, color: "",}))}
                                                        className="shrink-0 text-xs font-semibold text-gray-500 hover:text-red-600 hover:underline"
                                                    >
                                                        Ripristina
                                                    </button>
                                                )}
                                            </div>

                                            <div className="flex flex-wrap items-center gap-2">
                                                {/* COLORI PREDEFINITI */}
                                                {AREA_COLOR_PRESETS.map((color) => {
                                                    const selected = form.color?.toLowerCase() === color.toLowerCase();

                                                    return (
                                                        <button key={color} type="button"
                                                            onClick={() => setForm((prev) => ({ ...prev, color }))}
                                                            className={`
                                                                h-7 w-7 rounded-full
                                                                transition-all hover:scale-110
                                                                ${selected ? "ring-2 ring-indigo-500 ring-offset-2" : "ring-1 ring-black/10"}
                                                            `}
                                                            style={{backgroundColor: color,}}
                                                            title={color}
                                                        />
                                                    );
                                                })}

                                                {/* PICKER PERSONALIZZATO */}
                                                <div className="relative ml-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowColorPicker((prev) => !prev)}
                                                        className="inline-flex h-8 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-600
                                                            shadow-smhover:border-indigo-300 hover:bg-indigo-50"
                                                    >
                                                        <span
                                                            className="h-4 w-4 rounded-full ring-1 ring-black/10"
                                                            style={{backgroundColor: form.color || "#22c55e"}}
                                                        />
                                                        Personalizzato
                                                    </button>

                                                    {showColorPicker && (
                                                        <div className="absolute left-0 top-full z-[600] mt-2 w-[220px] rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
                                                            <HexColorPicker
                                                                color={form.color || "#22c55e"}
                                                                onChange={(color) => setForm((prev) => ({ ...prev, color }))}
                                                                style={{width: "100%"}}
                                                            />

                                                            <div className="mt-3 flex items-center gap-2">
                                                                <span
                                                                    className="h-8 w-8 shrink-0 rounded-md border border-gray-200"
                                                                    style={{ backgroundColor: form.color || "#22c55e" }}
                                                                />

                                                                <div className="flex min-w-0 flex-1 items-center overflow-hidden rounded-md border border-gray-300 bg-white px-2">
                                                                    <span className="shrink-0 text-xs text-gray-400">#</span>

                                                                    <HexColorInput
                                                                        color={form.color || "#22c55e"}
                                                                        onChange={(color) => setForm((prev) => ({ ...prev, color }))}
                                                                        prefixed={false}
                                                                        className="w-full min-w-0 flex-1 border-0 px-1 py-1.5 text-xs font-medium uppercase text-gray-700 outline-none focus:ring-0"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="mt-3 flex justify-end">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setShowColorPicker(false)}
                                                                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500"
                                                                >
                                                                    Fatto
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {formErrors.color && (
                                                <p className="text-xs text-red-600">
                                                    {Array.isArray(formErrors.color) ? formErrors.color[0] : formErrors.color}
                                                </p>
                                            )}
                                        </div>
                                        <hr className="h-0.5 bg-indigo-500 mb-2 " />
                                        {/* Indirizzo */}
                                        <div className="space-y-2 pb-3 mt-3">
                                            <label className="block text-sm font-medium text-gray-900">
                                                Indirizzo
                                                <span className="ml-1 text-gray-400 text-xs font-medium">
                                                    (facoltativo)
                                                </span>
                                            </label>

                                            <input
                                                type="text"
                                                maxLength={255}
                                                value={form.address || ""}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        // L'indirizzo è modificabile liberamente e non modifica
                                                        // latitude e longitude impostate tramite la mappa.
                                                        address: e.target.value,
                                                    }))
                                                }
                                                className={`w-full py-1.5 px-2 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500 placeholder:text-sm placeholder:text-gray-400 placeholder:font-medium ${
                                                    formErrors.address
                                                        ? "border-red-400"
                                                        : "border-gray-300"
                                                }`}
                                                placeholder={
                                                    isResolvingAddress
                                                        ? "Recupero indirizzo dalla posizione..."
                                                        : "Es. Piazza di Spagna, Roma"
                                                }
                                            />

                                            <p className="text-xs text-gray-500">
                                                Se selezioni un punto sulla mappa, l'indirizzo viene proposto automaticamente.
                                                Puoi modificarlo senza cambiare la posizione geografica impostata
                                            </p>

                                            {formErrors.address && (
                                                <p className="mt-1 text-xs text-red-600">
                                                    {Array.isArray(formErrors.address)
                                                        ? formErrors.address[0]
                                                        : formErrors.address}
                                                </p>
                                            )}
                                        </div>
                                        <hr className="h-0.5 bg-indigo-500 mb-2 " />
                                        {/* Posizione + raggio (opzionali) */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-900 mb-1 mt-4">
                                                Posizione sulla mappa{" "}
                                                <span className="ml-1 text-gray-400 text-xs font-medium">
                                                    (facoltativa)
                                                </span>
                                            </label>

                                            {isPickerOpen ? (
                                                <div className="border-y border-gray-300 p-3">
                                                    <LocationMapField
                                                        position={
                                                            form.latitude != null &&
                                                            form.longitude != null
                                                                ? [
                                                                    Number(form.latitude),
                                                                    Number(form.longitude),
                                                                ]
                                                                : null
                                                        }
                                                        onPositionChange={handlePositionChange}
                                                        onAddressResolved={handleAddressResolved}
                                                        radius={form.radius}
                                                        height="250px"
                                                    />
                                                    <div className="mt-2 flex justify-center gap-2">
                                                        <button
                                                            type="button"
                                                            className="text-xs w-20 font-semibold text-gray-600 hover:bg-gray-100 border border-gray-300 px-2 py-1 rounded-lg"
                                                            onClick={cancelPositionPicker}
                                                        >
                                                            Annulla
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="text-xs w-20 font-semibold text-indigo-600 hover:bg-indigo-100 border border-indigo-300 px-2 py-1 rounded-lg"
                                                            onClick={confirmPositionPicker}
                                                        >
                                                            Fatto
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : form.latitude != null && form.longitude != null ? (
                                                <div className="flex items-center justify-between gap-4 rounded-md border border-gray-300 px-3 py-2">
                                                    <div className="flex min-w-0 items-start gap-2">
                                                        <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />

                                                        <div className="min-w-0">
                                                            <div className="truncate text-sm text-gray-700">
                                                                Posizione geografica impostata
                                                            </div>

                                                            <div className="mt-0.5 text-xs text-gray-400">
                                                                Lat: {Number(form.latitude).toFixed(6)}
                                                                {" · "}
                                                                Lng: {Number(form.longitude).toFixed(6)}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex shrink-0 gap-3">
                                                        <button
                                                            type="button"
                                                            className="text-xs font-semibold text-indigo-600 hover:underline"
                                                            onClick={openPositionPicker}
                                                        >
                                                            Modifica
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="text-xs font-semibold text-red-600 hover:underline"
                                                            onClick={clearPosition}
                                                        >
                                                            Rimuovi
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={openPositionPicker}
                                                    className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 py-2 text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600"
                                                >
                                                    <MapPinIcon className="h-4 w-4" />
                                                    Seleziona posizione
                                                </button>
                                            )}

                                            {form.latitude != null && (
                                                <div className="mt-2 pb-3">
                                                    <label className="block text-sm font-medium text-gray-900 mb-1">
                                                        Raggio (metri)
                                                        <span className="ml-1 text-gray-400 text-xs font-medium">
                                                            - Si consiglia di impostare un raggio di almeno <span className="underline text-gray-600">100</span> metri poiché la precisione poiché la precisione della posizione rilevata dal dispositivo può variare
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={form.radius}
                                                        onChange={(e) =>
                                                            setForm((prev) => ({
                                                            ...prev,
                                                            radius: e.target.value,
                                                            }))
                                                        }
                                                        className={`w-32 py-1.5 px-2 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500 placeholder:text-sm placeholder:text-gray-400 placeholder:font-medium ${
                                                            formErrors.radius ? "border-red-400" : "border-gray-300"
                                                        }`}
                                                        placeholder="Es. 150"
                                                    />
                                                    {formErrors.radius && (
                                                        <p className="mt-1 text-xs text-red-600">
                                                            {formErrors.radius}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <hr className="h-0.5 bg-indigo-500 mb-2 mt-4" />
                                        {/* Validità dal/al (opzionali) */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-900 py-2">
                                                Validità{" "}
                                                <span className="ml-1 text-gray-400 text-xs font-medium">
                                                    (facoltativa — se vuota, l'area è sempre attiva)
                                                </span>
                                            </label>
                                            <div className="grid grid-cols-2 gap-3 pb-3">
                                                <div>
                                                    <label className="mb-1 flex items-center justify-between text-xs font-medium text-gray-600">
                                                        <span>Dal</span>

                                                        {form.valid_from && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setForm((prev) => ({
                                                                        ...prev,
                                                                        valid_from: "",
                                                                    }));

                                                                    setFormErrors((prev) => ({
                                                                        ...prev,
                                                                        valid_from: "",
                                                                        valid_to: "",
                                                                    }));
                                                                }}
                                                                className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 hover:underline"
                                                            >
                                                                Reset
                                                            </button>
                                                        )}
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={form.valid_from}
                                                        onChange={(e) =>
                                                            setForm((prev) => ({
                                                                ...prev,
                                                                valid_from: e.target.value,
                                                            }))
                                                        }
                                                        style={{
                                                            fontSize: "14px",
                                                            color: form.valid_from ? "#111827" : "#9ca3af",
                                                            fontWeight: 500,
                                                        }}
                                                        className={`w-full rounded-md border px-2 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                                                            formErrors.valid_from
                                                                ? "border-red-400"
                                                                : "border-gray-300"
                                                        }`}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 flex items-center justify-between text-xs font-medium text-gray-600">
                                                        <span>Al</span>

                                                        {form.valid_to && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setForm((prev) => ({
                                                                        ...prev,
                                                                        valid_to: "",
                                                                    }));

                                                                    setFormErrors((prev) => ({
                                                                        ...prev,
                                                                        valid_to: "",
                                                                    }));
                                                                }}
                                                                className="text-[11px] font-semibold text-indigo-600 transition-colors hover:text-indigo-700 hover:underline"
                                                            >
                                                                Reset
                                                            </button>
                                                        )}
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={form.valid_to}
                                                        onChange={(e) =>
                                                            setForm((prev) => ({
                                                                ...prev,
                                                                valid_to: e.target.value,
                                                            }))
                                                        }
                                                        style={{
                                                            fontSize: "14px",
                                                            color: form.valid_to ? "#111827" : "#9ca3af",
                                                            fontWeight: 500,
                                                        }}
                                                        className={`w-full rounded-md border px-2 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                                                            formErrors.valid_to
                                                                ? "border-red-400"
                                                                : "border-gray-300"
                                                        }`}
                                                    />
                                                    {formErrors.valid_to && (
                                                        <p className="mt-1 text-xs text-red-600">
                                                            {formErrors.valid_to}
                                                        </p>
                                                    )}
                                                </div>
                                                {form.valid_to && (
                                                    <div className="col-span-2 mt-2 rounded-lg border border-indigo-100 bg-indigo-50/40 p-4">
                                                        <div className="mb-3">
                                                            <p className="text-sm font-semibold text-indigo-800">
                                                                Promemoria scadenza area
                                                            </p>

                                                            <p className="mt-0.5 text-xs text-gray-500">
                                                                Ricevi un avviso via email prima della data di fine validità dell'area. Lascia vuoto se non vuoi ricevere il promemoria
                                                            </p>
                                                        </div>

                                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
                                                            <div>
                                                                <label className="mb-1 block text-xs font-medium text-gray-600">
                                                                    Giorni prima della scadenza
                                                                </label>

                                                                <input
                                                                    type="number"
                                                                    min={1}
                                                                    max={31}
                                                                    step={1}
                                                                    value={form.notification_timing}
                                                                    onChange={(e) => setForm((prev) => ({...prev, notification_timing: e.target.value,}))}
                                                                    className={`w-full rounded-md border px-2 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                                                                        formErrors.notification_timing
                                                                            ? "border-red-400"
                                                                            : "border-gray-300"
                                                                    }`}
                                                                    placeholder="Es. 7"
                                                                />

                                                                {formErrors.notification_timing && (
                                                                    <p className="mt-1 text-xs text-red-600">
                                                                        {Array.isArray(formErrors.notification_timing) ? formErrors.notification_timing[0] : formErrors.notification_timing}
                                                                    </p>
                                                                )}
                                                            </div>

                                                            <div>
                                                                <label className="mb-1 block text-xs font-medium text-gray-600">
                                                                    Email destinatari
                                                                </label>

                                                                <input
                                                                    type="text"
                                                                    value={form.notification_email}
                                                                    onChange={(e) => setForm((prev) => ({...prev, notification_email: e.target.value,}))}
                                                                    className={`w-full rounded-md border px-2 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                                                                        formErrors.notification_email ? "border-red-400" : "border-gray-300"
                                                                    }`}
                                                                    placeholder="es. amministrazione@azienda.it, responsabile@azienda.it"
                                                                />

                                                                <p className="mt-1 text-xs text-gray-500">
                                                                    Per inserire più destinatari separa gli indirizzi email con una virgola
                                                                </p>

                                                                {formErrors.notification_email && (
                                                                    <p className="mt-1 text-xs text-red-600">
                                                                        {Array.isArray(formErrors.notification_email) ? formErrors.notification_email[0] : formErrors.notification_email}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <hr className="h-0.5 bg-indigo-500 mb-2 " />
                                        {/* Identificativo area */}
                                        <div className="pt-2 pb-4">
                                            <div className="mb-2 flex items-center justify-between gap-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-900">
                                                        <TagIcon className="h-[22px] w-[22px] shrink-0 inline-block me-2 text-indigo-700 bg-indigo-100 rounded-full p-1" />
                                                        Giustificativo Identificazione Area
                                                        <span className="ml-1 text-xs font-medium text-gray-400">
                                                            (facoltativo)
                                                        </span>
                                                    </label>

                                                    <p className="mt-0.5 text-xs text-gray-500">
                                                        Seleziona un giustificativo di identificazione area esistente oppure creane uno nuovo
                                                    </p>
                                                </div>

                                                {areaIdentifiers.length > 0 && !showIdentifierForm && (
                                                    <div className="flex shrink-0 items-center gap-2">
                                                        {selectedAreaIdentifier && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setSelectedAreaIdentifier(null);

                                                                    setForm((prev) => ({
                                                                        ...prev,
                                                                        justification: null,
                                                                    }));
                                                                }}
                                                                title="Rimuovi giustificativo"
                                                                className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-semibold text-gray-600 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                                                            >
                                                                <XMarkIcon className="h-4 w-4" />
                                                                Rimuovi
                                                            </button>
                                                        )}

                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setIdentifierForm({
                                                                    code: "",
                                                                    description: "",
                                                                });
                                                                setIdentifierErrors({});
                                                                setShowIdentifierForm(true);
                                                            }}
                                                            className="rounded-md border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                                                        >
                                                            + Nuovo
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {areaIdentifiers.length > 0 && !showIdentifierForm ? (
                                                <div className="pb-2">
                                                    <ComboboxAutocomplete
                                                        options={areaIdentifiers.map((item) => ({
                                                            ...item,
                                                            name: `${item.code} ${item.description}`,
                                                        }))}
                                                        value={selectedAreaIdentifier}
                                                        onChange={(value) => {
                                                            setSelectedAreaIdentifier(value);

                                                            setForm((prev) => ({
                                                                ...prev,
                                                                justification: value?.id ?? null,
                                                            }));
                                                        }}
                                                        placeholder="Cerca giustificativo..."
                                                        bgActiveColor="bg-indigo-50"
                                                        textActiveColor="text-indigo-700"
                                                        borderClass="border-gray-300 rounded-md"
                                                        positionOptions="absolute"
                                                        openDirection="bottom"
                                                        zIndex="z-[500]"
                                                        displayValue={(item) =>
                                                            item
                                                                ? `${item.code} - ${item.description}`
                                                                : ""
                                                        }
                                                        renderOption={(item) => (
                                                            <div className="flex items-center gap-3">
                                                                <span className="inline-flex min-w-[42px] justify-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-bold text-indigo-700">
                                                                    {item.code}
                                                                </span>

                                                                <span className="truncate text-sm text-gray-700">
                                                                    {item.description}
                                                                </span>
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            ) : (
                                                <IdentifierForm
                                                    value={identifierForm}
                                                    errors={identifierErrors}
                                                    loading={isSavingIdentifier}
                                                    onChange={setIdentifierForm}
                                                    onCancel={
                                                        areaIdentifiers.length > 0
                                                            ? () => {
                                                                setShowIdentifierForm(false);
                                                                setIdentifierForm({
                                                                    code: "",
                                                                    description: "",
                                                                });
                                                                setIdentifierErrors({});
                                                            }
                                                            : null
                                                    }
                                                    onSave={handleCreateIdentifier}
                                                />
                                            )}
                                        </div>
                                        <hr className="h-0.5 bg-indigo-500 mb-2 " />
                                        {/* NFC Tag */}
                                        <div className="py-2 pb-4">
                                            <div className="mb-2 flex items-center justify-between gap-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-900">
                                                        <TapAndPlayIcon className="h-[22px] w-[22px] shrink-0 inline-block me-2 text-indigo-600 bg-indigo-100 rounded-full p-1" />
                                                        NFC Tag
                                                        <span className="ml-1 text-xs font-medium text-gray-400">
                                                            (facoltativo)
                                                        </span>
                                                    </label>

                                                    <p className="mt-0.5 text-xs text-gray-500">
                                                        {isEmployee ? 'Seleziona un Tag se disponibile' : 'Seleziona un Tag se disponibile oppure fai richiesta di acquisto'}
                                                    </p>
                                                </div>

                                                {selectedNfcTag && (
                                                    <div className="flex shrink-0 items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedNfcTag(null);

                                                                setForm((prev) => ({
                                                                    ...prev,
                                                                    nfc_tag: null,
                                                                }));
                                                            }}
                                                            title="Rimuovi NFC"
                                                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-semibold text-gray-600 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                                                        >
                                                            <XMarkIcon className="h-4 w-4" />
                                                            Rimuovi
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {loadingNfcTags ? (
                                                <div className="flex justify-center py-5">
                                                    <Fetching />
                                                </div>
                                            ) : nfcTags.length > 0 ? (
                                                <ComboboxAutocomplete
                                                    options={nfcTags}
                                                    value={selectedNfcTag}
                                                    onChange={(value) => {
                                                        setSelectedNfcTag(value);
                                                        setForm((prev) => ({...prev, nfc_tag: value?.id ?? null,}));
                                                    }}
                                                    placeholder="Cerca NFC..."
                                                    bgActiveColor="bg-indigo-50"
                                                    textActiveColor="text-indigo-700"
                                                    borderClass="border-gray-300 rounded-md"
                                                    positionOptions="absolute"
                                                    openDirection="top"
                                                    zIndex="z-[500]"
                                                    displayValue={(tag) => tag ? tag.name || tag.token : ""}
                                                    renderOption={(tag) => (
                                                    <div className="flex min-w-0 items-center gap-3">
                                                        <span className="inline-flex min-w-[42px] shrink-0 justify-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-bold uppercase text-indigo-700">
                                                        NFC
                                                        </span>
                                                        <div className="min-w-0">
                                                            <p className="truncate text-sm font-medium text-gray-700">
                                                                {tag.name || "NFC senza nome"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    )}
                                                />
                                            ) : (
                                            <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                                                Non sono presenti tag NFC disponibili
                                            </p>
                                            )}
                                        </div>
                                        <hr className="h-0.5 bg-indigo-500 mb-2 " />
                                        {/* Note */}
                                        <div className="space-y-1 py-2">
                                            <label className="block text-sm font-medium text-gray-900">
                                                <DocumentTextIcon className="h-[22px] w-[22px] shrink-0 inline-block me-2 text-indigo-600 bg-indigo-100 rounded-full p-1" />
                                                Note
                                                <span className="ml-1 text-xs font-medium text-gray-400">
                                                    (facoltative)
                                                </span>
                                            </label>

                                            <textarea
                                                rows={2}
                                                value={form.notes || ""}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        notes: e.target.value,
                                                    }))
                                                }
                                                className={`w-full resize-y rounded-md border px-2 py-1.5 text-sm placeholder:text-sm placeholder:font-medium placeholder:text-gray-400 focus:border-indigo-500 focus:ring-indigo-500 ${
                                                    formErrors.notes
                                                        ? "border-red-400"
                                                        : "border-gray-300"
                                                }`}
                                                placeholder="Inserisci eventuali informazioni aggiuntive sull'area di lavoro"
                                            />

                                            {formErrors.notes && (
                                                <p className="mt-1 text-xs text-red-600">
                                                    {Array.isArray(formErrors.notes)
                                                        ? formErrors.notes[0]
                                                        : formErrors.notes}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="shrink-0 flex justify-end gap-2 border-t border-gray-200 px-5 py-4 rounded-b-lg bg-gray-50">
                                        <button
                                            type="button"
                                            onClick={closeForm}
                                            className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-semibold text-gray-800 bg-gray-200 hover:bg-gray-50"
                                        >
                                            Annulla
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSubmitForm}
                                            disabled={isSaving}
                                            className="rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                                        >
                                            {isSaving ? "Salvataggio..." : "Salva"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default WorkingAreaComponent;



const IdentifierForm = ({
    value,
    errors,
    loading,
    onChange,
    onSave,
    onCancel,
}) => {
    return (
        <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-4">
            <div className="mb-3">
                <p className="text-sm font-semibold text-indigo-800">
                    Nuovo Giustificativo Id Area
                </p>

                <p className="mt-0.5 text-xs text-gray-500">
                    Inserisci un codice univoco e una descrizione
                </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[140px_minmax(0,1fr)]">
                <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                        Codice
                        <span className="ml-1 text-red-500">*</span>
                    </label>

                    <input
                        type="text"
                        maxLength={3}
                        value={value.code}
                        onChange={(event) =>
                            onChange((prev) => ({
                                ...prev,
                                code: event.target.value
                                    .slice(0, 3),
                            }))
                        }
                        className={`w-full rounded-md border px-2 py-1.5 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 ${
                            errors.code
                                ? "border-red-400"
                                : "border-gray-300"
                        }`}
                        placeholder="Es. MAG"
                    />

                    {errors.code && (
                        <p className="mt-1 text-xs text-red-600">
                            {Array.isArray(errors.code)
                                ? errors.code[0]
                                : errors.code}
                        </p>
                    )}
                </div>

                <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                        Descrizione
                        <span className="ml-1 text-red-500">*</span>
                    </label>

                    <input
                        type="text"
                        maxLength={255}
                        value={value.description}
                        onChange={(event) => onChange((prev) => ({...prev, description: event.target.value,}))}
                        className={`w-full rounded-md border px-2 py-1.5 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 ${errors.description ? "border-red-400" : "border-gray-300"}`}
                        placeholder="Es. Area magazzino"
                    />

                    {errors.description && (
                        <p className="mt-1 text-xs text-red-600">{Array.isArray(errors.description) ? errors.description[0] : errors.description}</p>
                    )}
                </div>
            </div>

            <div className="mt-3 flex justify-end gap-2">
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        Annulla
                    </button>
                )}

                <button
                    type="button"
                    onClick={onSave}
                    disabled={loading}
                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {loading ? "Salvataggio..." : "Crea giustificativo"}
                </button>
            </div>
        </div>
    );
};
